import { Container, Graphics, Rectangle, Sprite, Text, TilingSprite, type Texture } from 'pixi.js';
import { bakeFrames } from '../art/bake';
import { COAT_BAY } from '../art/palettes';
import type { PaletteInstance } from '../art/types';
import { HORSE_IDLE } from '../art/sprites/horse';
import {
  BARN,
  CLOUD_BIG,
  CLOUD_SMALL,
  FEED_SHED,
  FENCE,
  FLAG_POST,
  GRASS_TILE,
  NOTICE_BOARD,
  STARTING_GATE,
  TREE_BIG,
  TREE_SMALL,
  type Showpiece,
} from '../art/sprites/scenery';
import type { FacilityId } from '../sim/ranch/economy';
import { VIRTUAL_H, VIRTUAL_W } from './StageScaler';

export interface RanchHotspots {
  onFacility(id: FacilityId): void;
  onBarn(): void;
  onFeed(): void;
  onBoard(): void;
}

interface Hotspot {
  root: Container;
  label: Text;
  pips: Graphics | null;
  level: number;
  baseY: number;
}

const LABEL_STYLE = {
  fontFamily: 'Courier New',
  fontSize: 9,
  fontWeight: 'bold' as const,
  fill: 0xefe8d8,
  stroke: { color: 0x1a140e, width: 2 },
};

/** The ranch, dressed: pixel buildings on a pasture, every zone a button. */
export class RanchRenderer {
  private readonly container = new Container();
  private readonly hotspots = new Map<string, Hotspot>();
  private readonly clouds: Sprite[] = [];
  private readonly idleHorse: Sprite;
  private idleFrames: Texture[];
  private readonly boardSprite: Sprite;

  constructor(parent: Container, handlers: RanchHotspots) {
    parent.addChild(this.container);

    const sky = new Graphics()
      .rect(0, 0, VIRTUAL_W, 30)
      .fill(0x8fb0d0)
      .rect(0, 30, VIRTUAL_W, 22)
      .fill(0x87a8c8)
      .rect(0, 52, VIRTUAL_W, 12)
      .fill(0x6f94b8);
    this.container.addChild(sky);

    const grassTex = bakeFrames(GRASS_TILE.def, GRASS_TILE.palette)[0]!;
    const pasture = new TilingSprite({ texture: grassTex, width: VIRTUAL_W, height: VIRTUAL_H - 64 });
    pasture.position.set(0, 64);
    this.container.addChild(pasture);

    for (const [piece, x, y] of [
      [CLOUD_BIG, 80, 18],
      [CLOUD_SMALL, 250, 8],
      [CLOUD_SMALL, 410, 26],
    ] as const) {
      const cloud = new Sprite(bakeFrames(piece.def, piece.palette)[0]!);
      cloud.position.set(x, y);
      this.container.addChild(cloud);
      this.clouds.push(cloud);
    }

    // Horizon treeline.
    this.putSprite(TREE_SMALL, 10, 78);
    this.putSprite(TREE_BIG, 198, 74);
    this.putSprite(TREE_SMALL, 468, 80);

    // --- Gallop track (facility 1): dirt strip, infield, rail, flags.
    const track = new Container();
    const trackGfx = new Graphics()
      .rect(208, 96, 260, 52)
      .fill(0x8a6a45)
      .rect(232, 108, 212, 28)
      .fill(0x6f9450);
    track.addChild(trackGfx);
    const fenceTex = bakeFrames(FENCE.def, FENCE.palette)[0]!;
    const trackRail = new TilingSprite({ texture: fenceTex, width: 256, height: FENCE.def.h });
    trackRail.position.set(210, 148 - FENCE.def.h + 6);
    track.addChild(trackRail);
    const flagTex = bakeFrames(FLAG_POST.def, FLAG_POST.palette)[0]!;
    for (const fx of [222, 446]) {
      const flag = new Sprite(flagTex);
      flag.position.set(fx, 96 - FLAG_POST.def.h + 10);
      track.addChild(flag);
    }
    this.addHotspot('gallopTrack', track, new Rectangle(208, 84, 260, 72), 'GALLOP TRACK (1)', 330, 84, true, () =>
      handlers.onFacility('gallopTrack'),
    );

    // --- Barn, feed shed, notice board.
    const barn = this.pieceContainer(BARN, 52, 140);
    this.addHotspot('barn', barn, new Rectangle(20, 86, 66, 58), 'BARN (B)', 52, 80, false, handlers.onBarn);

    const shed = this.pieceContainer(FEED_SHED, 122, 142);
    this.addHotspot('feed', shed, new Rectangle(101, 106, 42, 40), 'FEED (F)', 122, 100, false, handlers.onFeed);

    const board = this.pieceContainer(NOTICE_BOARD, 168, 146);
    this.boardSprite = board.children[0] as Sprite;
    this.addHotspot('board', board, new Rectangle(153, 106, 30, 44), 'RACES (N)', 168, 100, false, handlers.onBoard);

    // --- Endurance trail (facility 2): winding path with trees.
    const trail = new Container();
    const path = new Graphics();
    const points: [number, number][] = [
      [148, 236],
      [190, 246],
      [240, 238],
      [290, 250],
      [336, 242],
    ];
    for (let index = 0; index < points.length - 1; index += 1) {
      const [ax, ay] = points[index]!;
      const [bx, by] = points[index + 1]!;
      const steps = 24;
      for (let s = 0; s <= steps; s += 1) {
        const t = s / steps;
        path.rect(ax + (bx - ax) * t, ay + (by - ay) * t - 4, 3, 9).fill(0x8a7a5c);
      }
    }
    trail.addChild(path);
    this.putSpriteIn(trail, TREE_BIG, 168, 262);
    this.putSpriteIn(trail, TREE_SMALL, 252, 234);
    this.putSpriteIn(trail, TREE_SMALL, 318, 266);
    this.addHotspot('enduranceTrail', trail, new Rectangle(140, 222, 208, 46), 'TRAIL (2)', 244, 218, true, () =>
      handlers.onFacility('enduranceTrail'),
    );

    // --- Starting gates (facility 3).
    const gates = this.pieceContainer(STARTING_GATE, 408, 252);
    this.addHotspot('startingGate', gates, new Rectangle(368, 208, 82, 48), 'GATES (3)', 408, 204, true, () =>
      handlers.onFacility('startingGate'),
    );

    // --- Round pen (facility 4): dirt ring with rails front and back.
    const pen = new Container();
    const penGfx = new Graphics().ellipse(64, 220, 44, 22).fill(0x8a7a5c);
    pen.addChild(penGfx);
    const backRail = new TilingSprite({ texture: fenceTex, width: 72, height: FENCE.def.h });
    backRail.position.set(28, 198 - FENCE.def.h + 8);
    pen.addChild(backRail);
    const frontRail = new TilingSprite({ texture: fenceTex, width: 88, height: FENCE.def.h });
    frontRail.position.set(20, 234 - FENCE.def.h + 12);
    pen.addChild(frontRail);
    this.addHotspot('roundPen', pen, new Rectangle(16, 192, 98, 56), 'PEN (4)', 64, 186, true, () =>
      handlers.onFacility('roundPen'),
    );

    // The featured horse, grazing between the barn and the track.
    this.idleFrames = bakeFrames(HORSE_IDLE, COAT_BAY);
    this.idleHorse = new Sprite(this.idleFrames[0]!);
    this.idleHorse.position.set(120 - HORSE_IDLE.anchor[0], 178 - HORSE_IDLE.h);
    this.container.addChild(this.idleHorse);
  }

  /** The grazing horse wears the featured horse's genetic coat. */
  setCoat(palette: PaletteInstance | null): void {
    this.idleFrames = bakeFrames(HORSE_IDLE, palette ?? COAT_BAY);
    this.idleHorse.visible = palette !== null;
  }

  private putSprite(piece: Showpiece, x: number, groundY: number): void {
    this.putSpriteIn(this.container, piece, x, groundY);
  }

  private putSpriteIn(parent: Container, piece: Showpiece, x: number, groundY: number): void {
    const sprite = new Sprite(bakeFrames(piece.def, piece.palette)[0]!);
    sprite.position.set(x - piece.def.anchor[0], groundY - piece.def.h);
    parent.addChild(sprite);
  }

  private pieceContainer(piece: Showpiece, x: number, groundY: number): Container {
    const holder = new Container();
    this.putSpriteIn(holder, piece, x, groundY);
    return holder;
  }

  private addHotspot(
    key: string,
    content: Container,
    hitArea: Rectangle,
    labelText: string,
    labelCx: number,
    labelY: number,
    withPips: boolean,
    onTap: () => void,
  ): void {
    const root = new Container();
    root.addChild(content);
    const label = new Text({ text: labelText, style: LABEL_STYLE });
    label.position.set(Math.round(labelCx - label.width / 2), labelY);
    root.addChild(label);
    let pips: Graphics | null = null;
    if (withPips) {
      pips = new Graphics();
      pips.position.set(Math.round(labelCx - 14), labelY + 12);
      root.addChild(pips);
    }
    root.eventMode = 'static';
    root.cursor = 'pointer';
    root.hitArea = hitArea;
    root.on('pointertap', onTap);
    root.on('pointerover', () => {
      root.position.y = -2;
      label.style.fill = 0xd9a441;
    });
    root.on('pointerout', () => {
      root.position.y = 0;
      label.style.fill = 0xefe8d8;
    });
    this.container.addChild(root);
    const hotspot: Hotspot = { root, label, pips, level: 1, baseY: labelY };
    this.hotspots.set(key, hotspot);
    if (pips) this.drawPips(hotspot);
  }

  private drawPips(hotspot: Hotspot): void {
    if (!hotspot.pips) return;
    hotspot.pips.clear();
    for (let index = 0; index < 4; index += 1) {
      hotspot.pips
        .rect(index * 7, 0, 5, 5)
        .fill(index < hotspot.level ? 0xd9a441 : 0x3a3226)
        .rect(index * 7, 0, 5, 1)
        .fill(index < hotspot.level ? 0xefd9a0 : 0x2a241b);
    }
  }

  setFacilityLevel(id: FacilityId, level: number): void {
    const hotspot = this.hotspots.get(id);
    if (!hotspot) return;
    hotspot.level = level;
    this.drawPips(hotspot);
  }

  /** Ambient life: drifting clouds, grazing horse, race-day board pulse. */
  update(nowS: number, racePending: boolean): void {
    this.clouds.forEach((cloud, index) => {
      const speed = 2.4 - index * 0.6;
      const span = VIRTUAL_W + 80;
      cloud.x = ((((80 + index * 165 + nowS * speed) % span) + span) % span) - 60;
    });
    this.idleHorse.texture = this.idleFrames[Math.floor(nowS * 1.6) % this.idleFrames.length]!;
    this.boardSprite.tint = racePending ? (Math.sin(nowS * 6) > 0 ? 0xffe9a0 : 0xffffff) : 0xffffff;
  }

  set visible(value: boolean) {
    this.container.visible = value;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
